// ==================== TEMPLATES PAGE ====================

import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FileText,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  AlertCircle,
  CheckCircle,
  X,
  Search,
  Send,
  Clock,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Eye,
  MoreVertical,
  Copy,
  MessageSquare,
  Megaphone,
  Shield,
  Image as ImageIcon,
  Upload,
  Video,
  File,
  Play,
  ExternalLink,
} from 'lucide-react';
import { Button, Card, Badge, Input, Modal, Select, ConfirmDialog, Dropdown, Pagination } from '../../../components/ui';
import { templatesService, type TemplateItem, type TemplateStats, type CreateTemplateData } from '../../../services/templates.service';
import { useOrganizations } from '../../../hooks/useOrganizations';
import {
  TEMPLATE_CATEGORY_OPTIONS,
  TEMPLATE_FILTER_STATUS_OPTIONS,
  TEMPLATE_FILTER_CATEGORY_OPTIONS,
  TEMPLATE_HEADER_TYPE_OPTIONS,
  TEMPLATE_QUICK_REPLY_TYPE_OPTIONS,
  TEMPLATE_CTA_TYPE_OPTIONS,
  TEMPLATE_URL_TYPE_OPTIONS,
  TEMPLATE_COUNTRY_CODE_OPTIONS,
  TEMPLATE_LANGUAGE_OPTIONS,
  TEMPLATE_MAX_BUTTONS,
  TEMPLATE_BUTTON_LIST_THRESHOLD,
  TEMPLATE_EMOJI_CATEGORIES,
  TEMPLATE_VARIABLE_TYPES,
  TEMPLATE_BODY_MAX_LENGTH,
  getNextVariableNumber,
  countTemplateVariables,
  detectVariableType,
  extractAllVariables,
  validateVariablePosition,
} from '../../../constants';

// ==================== STATUS BADGE COMPONENT ====================

const StatusBadge = ({ status }: { status: TemplateItem['status'] }) => {
  const config = {
    DRAFT: { variant: 'default' as const, label: 'Draft', icon: FileText },
    PENDING_APPROVAL: { variant: 'warning' as const, label: 'Pending', icon: Clock },
    APPROVED: { variant: 'success' as const, label: 'Approved', icon: CheckCircle2 },
    REJECTED: { variant: 'error' as const, label: 'Rejected', icon: XCircle },
  };

  const { variant, label, icon: Icon } = config[status];

  return (
    <Badge variant={variant} className="flex items-center gap-1 text-xs whitespace-nowrap">
      <Icon className="w-3 h-3 shrink-0" />
      {label}
    </Badge>
  );
};

// ==================== CATEGORY BADGE COMPONENT ====================

const CategoryBadge = ({ category }: { category: TemplateItem['category'] }) => {
  const config = {
    MARKETING: { icon: Megaphone, className: 'bg-purple-500/10 text-purple-400 border-purple-500/30' },
    UTILITY: { icon: MessageSquare, className: 'bg-blue-500/10 text-blue-400 border-blue-500/30' },
    AUTHENTICATION: { icon: Shield, className: 'bg-green-500/10 text-green-400 border-green-500/30' },
  };

  const { icon: Icon, className } = config[category];

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border ${className}`}>
      <Icon className="w-3 h-3" />
      {category.charAt(0) + category.slice(1).toLowerCase()}
    </span>
  );
};

// ==================== TEMPLATE ITEM COMPONENT ====================

interface TemplateItemProps {
  template: TemplateItem;
  onEdit: () => void;
  onDelete: () => void;
  onSubmit: () => void;
  onDuplicate: () => void;
  onPreview: () => void;
}

const TemplateItemRow = ({ template, onEdit, onDelete, onSubmit, onDuplicate, onPreview }: TemplateItemProps) => {
  const dropdownItems = [
    {
      key: 'preview',
      label: 'Preview',
      icon: <Eye className="w-4 h-4" />,
      onClick: onPreview,
    },
    {
      key: 'edit',
      label: 'Edit',
      icon: <Pencil className="w-4 h-4" />,
      onClick: onEdit,
      disabled: template.status === 'APPROVED',
    },
    {
      key: 'duplicate',
      label: 'Duplicate',
      icon: <Copy className="w-4 h-4" />,
      onClick: onDuplicate,
    },
    {
      key: 'submit',
      label: 'Submit for Approval',
      icon: <Send className="w-4 h-4" />,
      onClick: onSubmit,
      disabled: template.status !== 'DRAFT' && template.status !== 'REJECTED',
    },
    {
      key: 'delete',
      label: 'Delete',
      icon: <Trash2 className="w-4 h-4" />,
      onClick: onDelete,
      colorClass: 'text-red-400 hover:text-red-300',
    },
  ];

  return (
    <div className="flex items-center gap-4 p-4 hover:bg-white/5 transition-colors">
      {/* Icon */}
      <div className="w-12 h-12 rounded-xl bg-orange-500/10 flex items-center justify-center shrink-0">
        <FileText className="w-6 h-6 text-orange-400" />
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h4 className="text-sm font-medium text-white truncate">{template.name}</h4>
          <StatusBadge status={template.status} />
          <CategoryBadge category={template.category} />
        </div>
        <p className="text-xs text-(--color-muted-gray) mt-1 truncate max-w-md">
          {template.bodyText}
        </p>
        <div className="flex items-center gap-4 mt-2 text-xs text-(--color-muted-gray)">
          <span>Language: {template.language}</span>
          <span>Created: {new Date(template.createdAt).toLocaleDateString()}</span>
        </div>
      </div>

      {/* Actions */}
      <div className="shrink-0">
        <Dropdown
          trigger={<MoreVertical className="w-4 h-4" />}
          items={dropdownItems}
          align="right"
        />
      </div>
    </div>
  );
};

// ==================== BUTTON EDITOR ====================

interface ButtonEditorProps {
  buttons: CreateTemplateData['buttons'];
  onChange: (buttons: CreateTemplateData['buttons']) => void;
}

type ButtonType = 'QUICK_REPLY' | 'URL' | 'PHONE' | 'COPY_CODE';

interface TemplateButton {
  type: ButtonType;
  text: string;
  url?: string;
  urlType?: 'STATIC' | 'DYNAMIC';
  phoneNumber?: string;
  countryCode?: string;
  offerCode?: string;
}

const ButtonEditor = ({ buttons = [], onChange }: ButtonEditorProps) => {
  // Separate buttons by category
  const quickReplyButtons = buttons?.filter(btn => btn.type === 'QUICK_REPLY') || [];
  const ctaButtons = buttons?.filter(btn => btn.type !== 'QUICK_REPLY') || [];
  
  const totalButtons = (buttons?.length || 0);
  const canAddMore = totalButtons < TEMPLATE_MAX_BUTTONS;

  // Check for duplicate button texts
  const getDuplicateTexts = (): Set<string> => {
    const texts = buttons?.map(btn => btn.text?.trim().toLowerCase()).filter(Boolean) || [];
    const seen = new Set<string>();
    const duplicates = new Set<string>();
    
    texts.forEach(text => {
      if (text && seen.has(text)) {
        duplicates.add(text);
      }
      if (text) seen.add(text);
    });
    
    return duplicates;
  };

  const duplicateTexts = getDuplicateTexts();
  const hasDuplicates = duplicateTexts.size > 0;

  // Check if a specific button has duplicate text
  const isDuplicateText = (text: string): boolean => {
    if (!text?.trim()) return false;
    return duplicateTexts.has(text.trim().toLowerCase());
  };

  // Get default button text based on type
  const getDefaultButtonText = (type: ButtonType): string => {
    switch (type) {
      case 'QUICK_REPLY':
        return 'Quick Reply';
      case 'URL':
        return 'Visit website';
      case 'PHONE':
        return 'Call us';
      case 'COPY_CODE':
        return 'Copy offer code';
      default:
        return '';
    }
  };

  const addButton = (type: ButtonType) => {
    if (!canAddMore) return;
    
    // Generate unique default text if there are existing buttons with the same default
    let defaultText = getDefaultButtonText(type);
    const existingTexts = buttons?.map(btn => btn.text?.toLowerCase()) || [];
    
    // If the default text already exists, add a number suffix
    if (existingTexts.includes(defaultText.toLowerCase())) {
      let counter = 2;
      while (existingTexts.includes(`${defaultText.toLowerCase()} ${counter}`)) {
        counter++;
      }
      defaultText = `${defaultText} ${counter}`;
    }
    
    const newButton: TemplateButton = { type, text: defaultText };
    
    // Set defaults based on type
    if (type === 'URL') {
      newButton.urlType = 'STATIC';
      newButton.url = '';
    } else if (type === 'PHONE') {
      newButton.countryCode = '+1';
      newButton.phoneNumber = '';
    } else if (type === 'COPY_CODE') {
      newButton.offerCode = '';
    }
    
    onChange([...(buttons || []), newButton]);
  };

  const updateButton = (index: number, updates: Partial<TemplateButton>) => {
    const newButtons = [...(buttons || [])] as TemplateButton[];
    newButtons[index] = { ...newButtons[index], ...updates };
    onChange(newButtons);
  };

  const removeButton = (index: number) => {
    onChange(buttons?.filter((_, i) => i !== index));
  };

  const getButtonIndex = (btn: TemplateButton): number => {
    return buttons?.findIndex(b => b === btn) ?? -1;
  };

  return (
    <div className="space-y-4">
      {/* Info banner */}
      <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-500/10 border border-blue-500/20 text-sm text-blue-300">
        <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
        <span>
          Add up to {TEMPLATE_MAX_BUTTONS} buttons. If you add more than {TEMPLATE_BUTTON_LIST_THRESHOLD} buttons, they will appear in a list.
        </span>
      </div>

      {/* Quick Reply Section */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium text-(--color-soft-gray)">Quick Reply · Optional</h4>
          <span className="text-xs text-(--color-muted-gray)">{quickReplyButtons.length} added</span>
        </div>
        
        {quickReplyButtons.map((btn) => {
          const index = getButtonIndex(btn);
          return (
            <div key={index} className="flex items-start gap-2 p-3 rounded-lg bg-(--color-charcoal-black) border border-(--color-border-gray)">
              {/* Drag handle */}
              <div className="flex items-center justify-center w-6 h-8 text-(--color-muted-gray) cursor-grab">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                  <circle cx="9" cy="6" r="1.5" />
                  <circle cx="15" cy="6" r="1.5" />
                  <circle cx="9" cy="12" r="1.5" />
                  <circle cx="15" cy="12" r="1.5" />
                  <circle cx="9" cy="18" r="1.5" />
                  <circle cx="15" cy="18" r="1.5" />
                </svg>
              </div>
              
              <div className="flex-1 space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs text-(--color-muted-gray) mb-1">Type</label>
                    <Select
                      options={TEMPLATE_QUICK_REPLY_TYPE_OPTIONS}
                      value={btn.type}
                      onChange={() => {}}
                      disabled
                      size="sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-(--color-muted-gray) mb-1">Button Text</label>
                    <div className="relative">
                      <Input
                        placeholder="Quick Reply"
                        value={btn.text}
                        onChange={(e) => updateButton(index, { text: e.target.value })}
                        maxLength={25}
                        error={isDuplicateText(btn.text) ? 'Duplicate button text' : undefined}
                      />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-(--color-muted-gray)">
                        {btn.text?.length || 0}/25
                      </span>
                    </div>
                    {isDuplicateText(btn.text) && (
                      <p className="text-xs text-red-400 mt-1">Button text must be unique</p>
                    )}
                  </div>
                </div>
              </div>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeButton(index)}
                className="text-(--color-muted-gray) hover:text-red-400"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          );
        })}
        
        {canAddMore && (
          <Button 
            variant="secondary" 
            size="sm" 
            onClick={() => addButton('QUICK_REPLY')} 
            className="w-full border-dashed text-(--color-muted-gray)"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Quick Reply
          </Button>
        )}
      </div>

      {/* Call to Action Section */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium text-(--color-soft-gray)">Call to Action · Optional</h4>
          <span className="text-xs text-(--color-muted-gray)">{ctaButtons.length} added</span>
        </div>
        
        {ctaButtons.map((btn) => {
          const index = getButtonIndex(btn);
          return (
            <div key={index} className="flex items-start gap-2 p-3 rounded-lg bg-(--color-charcoal-black) border border-(--color-border-gray)">
              {/* Drag handle */}
              <div className="flex items-center justify-center w-6 h-8 text-(--color-muted-gray) cursor-grab">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                  <circle cx="9" cy="6" r="1.5" />
                  <circle cx="15" cy="6" r="1.5" />
                  <circle cx="9" cy="12" r="1.5" />
                  <circle cx="15" cy="12" r="1.5" />
                  <circle cx="9" cy="18" r="1.5" />
                  <circle cx="15" cy="18" r="1.5" />
                </svg>
              </div>
              
              <div className="flex-1 space-y-2">
                {/* Type and Button Text Row */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs text-(--color-muted-gray) mb-1">Type of Action</label>
                    <Select
                      options={TEMPLATE_CTA_TYPE_OPTIONS}
                      value={btn.type}
                      size="sm"
                      onChange={(value) => {
                        const newType = (Array.isArray(value) ? value[0] : value) as ButtonType;
                        const updates: Partial<TemplateButton> = { type: newType };
                        
                        // Reset type-specific fields
                        if (newType === 'URL') {
                          updates.urlType = 'STATIC';
                          updates.url = '';
                          updates.phoneNumber = undefined;
                          updates.countryCode = undefined;
                          updates.offerCode = undefined;
                        } else if (newType === 'PHONE') {
                          updates.countryCode = '+1';
                          updates.phoneNumber = '';
                          updates.url = undefined;
                          updates.urlType = undefined;
                          updates.offerCode = undefined;
                        } else if (newType === 'COPY_CODE') {
                          updates.offerCode = '';
                          updates.url = undefined;
                          updates.urlType = undefined;
                          updates.phoneNumber = undefined;
                          updates.countryCode = undefined;
                        }
                        
                        updateButton(index, updates);
                      }}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-(--color-muted-gray) mb-1">Button Text</label>
                    <div className="relative">
                      <Input
                        placeholder={btn.type === 'URL' ? 'Visit website' : btn.type === 'PHONE' ? 'Call phone number' : 'Copy offer code'}
                        value={btn.text}
                        onChange={(e) => updateButton(index, { text: e.target.value })}
                        maxLength={25}
                        error={isDuplicateText(btn.text) ? 'Duplicate button text' : undefined}
                      />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-(--color-muted-gray)">
                        {btn.text?.length || 0}/25
                      </span>
                    </div>
                    {isDuplicateText(btn.text) && (
                      <p className="text-xs text-red-400 mt-1">Button text must be unique</p>
                    )}
                  </div>
                </div>

                {/* URL-specific fields */}
                {btn.type === 'URL' && (
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="block text-xs text-(--color-muted-gray) mb-1">URL Type</label>
                      <Select
                        options={TEMPLATE_URL_TYPE_OPTIONS}
                        value={btn.urlType || 'STATIC'}
                        size="sm"
                        onChange={(value) => updateButton(index, { urlType: (Array.isArray(value) ? value[0] : value) as 'STATIC' | 'DYNAMIC' })}
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-xs text-(--color-muted-gray) mb-1">Website URL</label>
                      <div className="relative">
                        <Input
                          placeholder="https://www.example.com"
                          value={btn.url || ''}
                          onChange={(e) => updateButton(index, { url: e.target.value })}
                          maxLength={2000}
                        />
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-(--color-muted-gray)">
                          {btn.url?.length || 0}/2000
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Phone-specific fields */}
                {btn.type === 'PHONE' && (
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="block text-xs text-(--color-muted-gray) mb-1">Country</label>
                      <Select
                        options={TEMPLATE_COUNTRY_CODE_OPTIONS}
                        value={btn.countryCode || '+1'}
                        size="sm"
                        onChange={(value) => updateButton(index, { countryCode: Array.isArray(value) ? value[0] : value })}
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-xs text-(--color-muted-gray) mb-1">Phone number (without leading 0)</label>
                      <div className="relative">
                        <Input
                          placeholder="7911123456"
                          value={btn.phoneNumber || ''}
                          onChange={(e) => {
                            // Remove non-digits and leading zeros
                            let cleaned = e.target.value.replace(/\D/g, '');
                            // Remove leading zeros
                            cleaned = cleaned.replace(/^0+/, '');
                            updateButton(index, { phoneNumber: cleaned });
                          }}
                          maxLength={15}
                        />
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-(--color-muted-gray)">
                          {btn.phoneNumber?.length || 0}/15
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Copy Code-specific fields */}
                {btn.type === 'COPY_CODE' && (
                  <div>
                    <label className="block text-xs text-(--color-muted-gray) mb-1">Offer code</label>
                    <div className="relative">
                      <Input
                        placeholder="Enter sample"
                        value={btn.offerCode || ''}
                        onChange={(e) => updateButton(index, { offerCode: e.target.value })}
                        maxLength={20}
                      />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-(--color-muted-gray)">
                        {btn.offerCode?.length || 0}/20
                      </span>
                    </div>
                  </div>
                )}
              </div>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeButton(index)}
                className="text-(--color-muted-gray) hover:text-red-400"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          );
        })}
        
        {canAddMore && (
          <Dropdown
            trigger={
              <div className="w-full flex items-center justify-center gap-2 py-2 px-4 text-sm font-medium text-(--color-muted-gray) border border-dashed border-(--color-border-gray) rounded-lg hover:border-(--color-soft-gray) hover:text-(--color-soft-gray) transition-colors cursor-pointer">
                <Plus className="w-4 h-4" />
                Add Call to Action
              </div>
            }
            items={TEMPLATE_CTA_TYPE_OPTIONS.map(opt => ({
              key: opt.value,
              label: opt.label,
              icon: opt.value === 'URL' ? (
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                  <polyline points="15 3 21 3 21 9" />
                  <line x1="10" y1="14" x2="21" y2="3" />
                </svg>
              ) : opt.value === 'PHONE' ? (
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                </svg>
              ) : (
                <Copy className="w-4 h-4" />
              ),
              onClick: () => addButton(opt.value as ButtonType),
            }))}
            align="left"
          />
        )}
      </div>

      {/* Button count summary */}
      <div className="flex items-center justify-between pt-2 border-t border-(--color-border-gray)">
        <span className="text-xs text-(--color-muted-gray)">
          Total buttons: {totalButtons}/{TEMPLATE_MAX_BUTTONS}
        </span>
        <div className="flex items-center gap-3">
          {hasDuplicates && (
            <span className="text-xs text-red-400">
              ⚠️ Duplicate button texts found
            </span>
          )}
          {totalButtons > TEMPLATE_BUTTON_LIST_THRESHOLD && (
            <span className="text-xs text-amber-400">
              ⚠️ Buttons will appear in a list
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

// ==================== BODY TEXT EDITOR ====================

interface VariableSample {
  variable: string;
  sample: string;
}

interface BodyTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  maxLength?: number;
  placeholder?: string;
  variableSamples?: VariableSample[];
  onVariableSamplesChange?: (samples: VariableSample[]) => void;
}

const BodyTextEditor = ({ 
  value, 
  onChange, 
  maxLength = TEMPLATE_BODY_MAX_LENGTH, 
  placeholder,
  variableSamples = [],
  onVariableSamplesChange,
}: BodyTextEditorProps) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showVariableDropdown, setShowVariableDropdown] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(0);
  const emojiPickerRef = useRef<HTMLDivElement>(null);
  const variableDropdownRef = useRef<HTMLDivElement>(null);

  // Count variables in the text
  const variableCount = countTemplateVariables(value);
  
  // Detect which variable type is currently in use
  const currentVarType = detectVariableType(value);
  
  // Extract all variables from body text
  const extractedVariables = extractAllVariables(value);
  
  // Validate variable position
  const variablePositionValidation = validateVariablePosition(value);
  
  // Update sample values when variables change
  useEffect(() => {
    if (!onVariableSamplesChange) return;
    
    // Create samples array based on extracted variables
    const newSamples = extractedVariables.map(variable => {
      // Find existing sample for this variable
      const existingSample = variableSamples.find(s => s.variable === variable);
      return {
        variable,
        sample: existingSample?.sample || '',
      };
    });
    
    // Only update if the variables list has changed
    const currentVars = variableSamples.map(s => s.variable).join(',');
    const newVars = newSamples.map(s => s.variable).join(',');
    
    if (currentVars !== newVars) {
      onVariableSamplesChange(newSamples);
    }
  }, [extractedVariables.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Handle sample value change
  const handleSampleChange = (variable: string, sampleValue: string) => {
    if (!onVariableSamplesChange) return;
    
    const newSamples = variableSamples.map(s => 
      s.variable === variable ? { ...s, sample: sampleValue } : s
    );
    onVariableSamplesChange(newSamples);
  };

  // Insert text at cursor position
  const insertAtCursor = (textToInsert: string) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newValue = value.slice(0, start) + textToInsert + value.slice(end);
    
    onChange(newValue);

    // Restore cursor position after the inserted text
    setTimeout(() => {
      textarea.focus();
      const newPosition = start + textToInsert.length;
      textarea.setSelectionRange(newPosition, newPosition);
    }, 0);
  };

  // Wrap selected text with formatting
  const wrapSelection = (prefix: string, suffix: string = prefix) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.slice(start, end);

    if (selectedText) {
      // Wrap the selected text
      const newValue = value.slice(0, start) + prefix + selectedText + suffix + value.slice(end);
      onChange(newValue);
      
      // Select the wrapped text (including formatting)
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start, end + prefix.length + suffix.length);
      }, 0);
    } else {
      // Insert formatting markers with cursor in between
      const newValue = value.slice(0, start) + prefix + suffix + value.slice(end);
      onChange(newValue);
      
      // Position cursor between the markers
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + prefix.length, start + prefix.length);
      }, 0);
    }
  };

  // Handle emoji selection
  const handleEmojiSelect = (emoji: string) => {
    insertAtCursor(emoji);
    setShowEmojiPicker(false);
  };

  // Handle variable type selection
  const handleVariableTypeSelect = (type: string) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    
    if (type === 'NUMBER') {
      const nextNum = getNextVariableNumber(value);
      insertAtCursor(`{{${nextNum}}}`);
    } else if (type === 'NAME') {
      // Insert {{}} and position cursor inside the braces
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newValue = value.slice(0, start) + '{{}}' + value.slice(end);
      onChange(newValue);
      
      // Position cursor between the braces
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + 2, start + 2);
      }, 0);
    }
    setShowVariableDropdown(false);
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(e.target as Node)) {
        setShowEmojiPicker(false);
      }
      if (variableDropdownRef.current && !variableDropdownRef.current.contains(e.target as Node)) {
        setShowVariableDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative">
      {/* Textarea */}
      <div className="relative">
        <textarea
          ref={textareaRef}
          placeholder={placeholder || "Enter your message body..."}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={5}
          maxLength={maxLength}
          className="w-full px-4 py-3 pb-14 rounded-lg bg-(--color-charcoal-black) border border-(--color-border-gray) text-white placeholder:text-(--color-muted-gray) focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500 transition-all resize-none"
        />
        
        {/* Toolbar at bottom of textarea */}
        <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between px-3 py-2 border-t border-(--color-border-gray) bg-(--color-charcoal-black) rounded-b-lg">
          <div className="flex items-center gap-1">
            {/* Emoji Picker */}
            <div className="relative" ref={emojiPickerRef}>
              <button
                type="button"
                onClick={() => {
                  setShowEmojiPicker(!showEmojiPicker);
                  setShowVariableDropdown(false);
                }}
                className="p-1.5 rounded hover:bg-(--color-border-gray) text-(--color-muted-gray) hover:text-white transition-colors"
                title="Insert emoji"
              >
                <span className="text-lg">😊</span>
              </button>
              
              {/* Emoji Picker Dropdown */}
              {showEmojiPicker && (
                <div className="absolute bottom-full left-0 mb-2 w-72 bg-[#1a1a1a] border border-(--color-border-gray) rounded-lg shadow-2xl z-50 backdrop-blur-none">
                  {/* Category tabs */}
                  <div className="flex bg-[#1a1a1a] border-b border-(--color-border-gray) overflow-x-auto [&::-webkit-scrollbar]:hidden rounded-t-lg">
                    {TEMPLATE_EMOJI_CATEGORIES.map((cat, idx) => (
                      <button
                        key={cat.name}
                        type="button"
                        onClick={() => setSelectedCategory(idx)}
                        className={`px-3 py-2 text-xs whitespace-nowrap transition-colors ${
                          selectedCategory === idx
                            ? 'text-orange-400 border-b-2 border-orange-400 -mb-px'
                            : 'text-(--color-muted-gray) hover:text-white'
                        }`}
                      >
                        {cat.name}
                      </button>
                    ))}
                  </div>
                  
                  {/* Emoji grid */}
                  <div className="p-2 max-h-48 overflow-y-auto bg-[#1a1a1a] rounded-b-lg">
                    <div className="grid grid-cols-8 gap-1">
                      {TEMPLATE_EMOJI_CATEGORIES[selectedCategory].emojis.map((emoji, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => handleEmojiSelect(emoji)}
                          className="p-1 text-xl hover:bg-(--color-border-gray) rounded transition-colors"
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Divider */}
            <div className="w-px h-5 bg-(--color-border-gray) mx-1" />
            
            {/* Bold */}
            <button
              type="button"
              onClick={() => wrapSelection('*')}
              className="p-1.5 rounded hover:bg-(--color-border-gray) text-(--color-muted-gray) hover:text-white transition-colors font-bold"
              title="Bold (*text*)"
            >
              B
            </button>
            
            {/* Italic */}
            <button
              type="button"
              onClick={() => wrapSelection('_')}
              className="p-1.5 rounded hover:bg-(--color-border-gray) text-(--color-muted-gray) hover:text-white transition-colors italic"
              title="Italic (_text_)"
            >
              I
            </button>
            
            {/* Strikethrough */}
            <button
              type="button"
              onClick={() => wrapSelection('~')}
              className="p-1.5 rounded hover:bg-(--color-border-gray) text-(--color-muted-gray) hover:text-white transition-colors line-through"
              title="Strikethrough (~text~)"
            >
              S
            </button>
            
            {/* Monospace */}
            <button
              type="button"
              onClick={() => wrapSelection('```')}
              className="p-1.5 rounded hover:bg-(--color-border-gray) text-(--color-muted-gray) hover:text-white transition-colors font-mono text-xs"
              title="Monospace (```text```)"
            >
              {'</>'}
            </button>
            
            {/* Divider */}
            <div className="w-px h-5 bg-(--color-border-gray) mx-1" />
            
            {/* Add Variable */}
            <div className="relative" ref={variableDropdownRef}>
              <button
                type="button"
                onClick={() => {
                  setShowVariableDropdown(!showVariableDropdown);
                  setShowEmojiPicker(false);
                }}
                className="flex items-center gap-1 px-2 py-1 rounded hover:bg-(--color-border-gray) text-(--color-muted-gray) hover:text-white transition-colors text-sm"
                title="Add variable"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>Add variable</span>
              </button>
              
              {/* Variable Type Dropdown */}
              {showVariableDropdown && (
                <div className="absolute bottom-full left-0 mb-2 w-56 bg-[#1a1a1a] border border-(--color-border-gray) rounded-lg shadow-2xl z-50 overflow-hidden">
                  {currentVarType && (
                    <div className="px-3 py-2 bg-orange-500/10 border-b border-(--color-border-gray)">
                      <p className="text-xs text-orange-400">
                        Currently using <span className="font-semibold">{currentVarType === 'NUMBER' ? 'Number' : 'Name'}</span> type variables.
                        Remove all to switch types.
                      </p>
                    </div>
                  )}
                  <div className="py-1">
                    {TEMPLATE_VARIABLE_TYPES.map((varType) => {
                      const isDisabled = currentVarType !== null && currentVarType !== varType.value;
                      const isActive = currentVarType === varType.value;
                      
                      return (
                        <button
                          key={varType.value}
                          type="button"
                          onClick={() => !isDisabled && handleVariableTypeSelect(varType.value)}
                          disabled={isDisabled}
                          className={`w-full px-3 py-2 text-left text-sm transition-colors flex items-center justify-between ${
                            isDisabled
                              ? 'text-(--color-muted-gray) opacity-50 cursor-not-allowed'
                              : 'text-(--color-soft-gray) hover:bg-(--color-border-gray) hover:text-white cursor-pointer'
                          } ${isActive ? 'bg-(--color-border-gray)' : ''}`}
                        >
                          <div className="flex items-center gap-2">
                            {isActive && <span className="text-orange-400">●</span>}
                            <span>{varType.label}</span>
                          </div>
                          <span className="text-xs text-(--color-muted-gray)">{varType.example}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Variable count badge */}
          {variableCount > 0 && (
            <div className="flex items-center gap-1 px-2 py-0.5 bg-orange-500/20 text-orange-400 rounded-full text-xs font-medium">
              <span>{variableCount}</span>
            </div>
          )}
        </div>
      </div>
      
      {/* Character count and validation error */}
      <div className="flex items-center justify-between mt-1">
        {!variablePositionValidation.valid && (
          <p className="text-xs text-red-400 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            {variablePositionValidation.error}
          </p>
        )}
        <p className="text-xs text-(--color-muted-gray) ml-auto">
          {value.length}/{maxLength}
        </p>
      </div>
      
      {/* Variable Samples Section */}
      {extractedVariables.length > 0 && onVariableSamplesChange && (
        <div className="mt-4 p-4 rounded-lg bg-(--color-charcoal-black) border border-(--color-border-gray)">
          <h4 className="text-sm font-medium text-(--color-soft-gray) mb-1">Variable Samples</h4>
          <p className="text-xs text-(--color-muted-gray) mb-3">
            Include samples of all variables in your message to help Meta review your template. 
            Remember not to include any customer information to protect your customer's privacy.
          </p>
          
          <div className="space-y-3">
            <div className="text-xs font-medium text-(--color-muted-gray) uppercase tracking-wide">Body</div>
            {variableSamples.map((sample) => (
              <div key={sample.variable} className="flex items-start gap-3">
                <div className="w-28 shrink-0 px-3 py-2 bg-(--color-jet-black) border border-(--color-border-gray) rounded text-sm text-(--color-muted-gray) text-center truncate" title={`{{${sample.variable}}}`}>
                  {`{{${sample.variable}}}`}
                </div>
                <div className="flex-1">
                  <input
                    type="text"
                    placeholder={`Enter content for {{${sample.variable}}}`}
                    value={sample.sample}
                    onChange={(e) => handleSampleChange(sample.variable, e.target.value)}
                    className="w-full px-3 py-2 rounded bg-(--color-jet-black) border border-(--color-border-gray) text-white placeholder:text-(--color-muted-gray) focus:outline-none focus:ring-1 focus:ring-orange-500/50 focus:border-orange-500 text-sm transition-all"
                  />
                  {!sample.sample && (
                    <p className="text-xs text-orange-400 mt-1">Add sample text</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== TEMPLATE PREVIEW ====================

interface TemplatePreviewProps {
  template: Partial<TemplateItem>;
  mediaPreviewUrl?: string | null;
}

// Helper to parse WhatsApp formatting (bold, italic, strikethrough, monospace)
const parseWhatsAppFormatting = (text: string): React.ReactNode => {
  if (!text) return null;
  
  const result: React.ReactNode[] = [];
  let keyCounter = 0;
  
  // Combined regex that matches all formatting patterns
  // Order matters: monospace (```) must come first
  const formatRegex = /(```[^`]+```|\*[^*\n]+\*|_[^_\n]+_|~[^~\n]+~)/g;
  
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  
  while ((match = formatRegex.exec(text)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      result.push(text.substring(lastIndex, match.index));
    }
    
    const matchedText = match[0];
    
    // Determine which format was matched and create appropriate element
    if (matchedText.startsWith('```') && matchedText.endsWith('```')) {
      // Monospace
      const content = matchedText.slice(3, -3);
      result.push(
        <code key={keyCounter++} className="bg-gray-200 px-1 rounded text-sm font-mono">
          {content}
        </code>
      );
    } else if (matchedText.startsWith('*') && matchedText.endsWith('*')) {
      // Bold
      const content = matchedText.slice(1, -1);
      result.push(<strong key={keyCounter++}>{content}</strong>);
    } else if (matchedText.startsWith('_') && matchedText.endsWith('_')) {
      // Italic
      const content = matchedText.slice(1, -1);
      result.push(<em key={keyCounter++}>{content}</em>);
    } else if (matchedText.startsWith('~') && matchedText.endsWith('~')) {
      // Strikethrough
      const content = matchedText.slice(1, -1);
      result.push(<s key={keyCounter++}>{content}</s>);
    }
    
    lastIndex = match.index + matchedText.length;
  }
  
  // Add remaining text after the last match
  if (lastIndex < text.length) {
    result.push(text.substring(lastIndex));
  }
  
  return result.length > 0 ? result : text;
};

// Get button icon based on type
const getButtonIcon = (type: string) => {
  switch (type) {
    case 'URL':
      return (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
          <polyline points="15 3 21 3 21 9" />
          <line x1="10" y1="14" x2="21" y2="3" />
        </svg>
      );
    case 'PHONE':
      return (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
        </svg>
      );
    case 'QUICK_REPLY':
      return (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="9 14 4 9 9 4" />
          <path d="M20 20v-7a4 4 0 0 0-4-4H4" />
        </svg>
      );
    case 'COPY_CODE':
      return (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
        </svg>
      );
    default:
      return null;
  }
};

const TemplatePreview = ({ template, mediaPreviewUrl }: TemplatePreviewProps) => {
  const [showAllOptions, setShowAllOptions] = useState(false);
  const currentTime = new Date().toLocaleTimeString('en-US', { 
    hour: '2-digit', 
    minute: '2-digit',
    hour12: true 
  });

  const hasMoreThanThreeButtons = (template.buttons?.length || 0) > TEMPLATE_BUTTON_LIST_THRESHOLD;

  // Separate quick replies from CTAs for the slide-up panel
  const quickReplyButtons = template.buttons?.filter(btn => btn.type === 'QUICK_REPLY') || [];
  const ctaButtons = template.buttons?.filter(btn => btn.type !== 'QUICK_REPLY') || [];

  // Determine the media source - use local preview if available, otherwise check if headerContent is a URL
  const getMediaSource = (): string | null => {
    if (mediaPreviewUrl) return mediaPreviewUrl;
    // If headerContent looks like a URL (for already uploaded templates), use it
    if (template.headerContent?.startsWith('http')) return template.headerContent;
    return null;
  };

  const mediaSource = getMediaSource();
  
  // Check if media has been uploaded (has a handle) but we don't have a preview URL
  const hasUploadedMedia = !mediaSource && template.headerContent && template.headerContent.length > 10;

  return (
    <div className="max-w-sm mx-auto">
      {/* Header with play button */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-medium text-white">Template Preview</h3>
        {hasMoreThanThreeButtons && (
          <button
            onClick={() => setShowAllOptions(true)}
            className="w-10 h-10 rounded-lg border border-(--color-border-gray) bg-(--color-charcoal-black) flex items-center justify-center hover:bg-white/5 transition-colors"
            title="Preview all options"
          >
            <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
              <path d="M8 5v14l11-7z" />
            </svg>
          </button>
        )}
      </div>
      
      {/* WhatsApp Chat Background */}
      <div 
        className="rounded-xl p-4 min-h-[300px] relative overflow-hidden"
        style={{
          backgroundColor: '#e5ddd5',
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23d4ccc4' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
      >
        {/* Message Bubble */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden max-w-[280px]">
          {/* Header - Image/Video/Document */}
          {template.headerType && (
            <>
              {template.headerType === 'TEXT' && template.headerContent && (
                <div className="px-3 pt-2">
                  <p className="font-semibold text-gray-900 text-[15px]">{template.headerContent}</p>
                </div>
              )}
              {template.headerType === 'IMAGE' && (
                <div className="relative">
                  {mediaSource ? (
                    <img 
                      src={mediaSource} 
                      alt="Header" 
                      className="w-full h-40 object-cover"
                    />
                  ) : hasUploadedMedia ? (
                    <div className="w-full h-40 bg-linear-to-br from-green-100 to-green-200 flex items-center justify-center">
                      <div className="text-center text-green-700">
                        <CheckCircle className="w-8 h-8 mx-auto mb-1" />
                        <span className="text-xs font-medium">Image Uploaded</span>
                        <p className="text-[10px] opacity-75 mt-0.5">Preview available after approval</p>
                      </div>
                    </div>
                  ) : (
                    <div className="w-full h-40 bg-linear-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                      <div className="text-center text-gray-500">
                        <ImageIcon className="w-10 h-10 mx-auto mb-1 opacity-50" />
                        <span className="text-xs">Image Header</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {template.headerType === 'VIDEO' && (
                <div className="relative w-full h-40 bg-gray-900 flex items-center justify-center overflow-hidden">
                  {mediaSource ? (
                    <>
                      <video 
                        src={mediaSource} 
                        className="max-h-full max-w-full object-contain"
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                        <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                          <Play className="w-5 h-5 text-white ml-0.5" />
                        </div>
                      </div>
                    </>
                  ) : hasUploadedMedia ? (
                    <div className="text-center text-green-400">
                      <CheckCircle className="w-8 h-8 mx-auto mb-1" />
                      <span className="text-xs font-medium">Video Uploaded</span>
                      <p className="text-[10px] opacity-75 mt-0.5">Preview available after approval</p>
                    </div>
                  ) : (
                    <div className="text-center text-white">
                      <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-1">
                        <Video className="w-5 h-5 text-white/75" />
                      </div>
                      <span className="text-xs opacity-75">Video Header</span>
                    </div>
                  )}
                </div>
              )}
              {template.headerType === 'DOCUMENT' && (
                <div className="p-3 bg-gray-50 border-b flex items-center gap-3">
                  <div className="w-10 h-12 bg-red-500 rounded flex items-center justify-center text-white text-xs font-bold">
                    PDF
                  </div>
                  <div className="flex-1 min-w-0">
                    {hasUploadedMedia ? (
                      <>
                        <p className="text-sm font-medium text-green-700 flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" />
                          Document Uploaded
                        </p>
                        <p className="text-xs text-gray-500">Available after approval</p>
                      </>
                    ) : (
                      <>
                        <p className="text-sm font-medium text-gray-900 truncate">
                          Document.pdf
                        </p>
                        <p className="text-xs text-gray-500">PDF Document</p>
                      </>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Body Text */}
          <div className="px-3 py-2">
            <p className="text-gray-800 text-[15px] leading-relaxed whitespace-pre-wrap">
              {parseWhatsAppFormatting(template.bodyText || 'Your message body will appear here...')}
            </p>
          </div>

          {/* Footer + Timestamp */}
          <div className="px-3 pb-2 flex items-end justify-between gap-2">
            {template.footerText && (
              <p className="text-xs text-gray-500 flex-1">{template.footerText}</p>
            )}
            <span className="text-[11px] text-gray-400 whitespace-nowrap ml-auto">
              {currentTime}
            </span>
          </div>

          {/* Action Buttons */}
          {template.buttons && template.buttons.length > 0 && (
            <>
              {/* Show first 3 buttons inline */}
              <div className="border-t border-gray-200">
                {template.buttons.slice(0, TEMPLATE_BUTTON_LIST_THRESHOLD).map((btn, index) => (
                  <div 
                    key={index}
                    className={`flex items-center justify-center gap-2 py-3 text-[#0088cc] font-medium text-[15px] cursor-pointer hover:bg-gray-50 transition-colors ${
                      index > 0 ? 'border-t border-gray-200' : ''
                    }`}
                  >
                    {getButtonIcon(btn.type)}
                    <span>{btn.text || 'Button'}</span>
                  </div>
                ))}
              </div>
              
              {/* Show "See all options" if more than 3 buttons */}
              {hasMoreThanThreeButtons && (
                <div className="border-t border-gray-200">
                  <div 
                    onClick={() => setShowAllOptions(true)}
                    className="flex items-center justify-center gap-2 py-3 text-[#0088cc] font-medium text-[15px] cursor-pointer hover:bg-gray-50 transition-colors"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="3" y1="12" x2="21" y2="12" />
                      <line x1="3" y1="6" x2="21" y2="6" />
                      <line x1="3" y1="18" x2="21" y2="18" />
                    </svg>
                    <span>See all options</span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Slide-up Panel Overlay */}
        <AnimatePresence>
          {showAllOptions && (
            <>
              {/* Backdrop */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="absolute inset-0 bg-black/30"
                onClick={() => setShowAllOptions(false)}
              />
              
              {/* Slide-up Panel */}
              <motion.div
                initial={{ y: '100%' }}
                animate={{ y: 0 }}
                exit={{ y: '100%' }}
                transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-xl"
              >
                {/* Handle */}
                <div className="flex justify-center pt-3 pb-2">
                  <div className="w-10 h-1 bg-gray-300 rounded-full" />
                </div>
                
                {/* Header */}
                <div className="flex items-center px-4 pb-2">
                  <button
                    onClick={() => setShowAllOptions(false)}
                    className="p-1 -ml-1 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                  <h4 className="flex-1 text-center text-sm font-medium text-gray-700">All Options</h4>
                  <div className="w-5" /> {/* Spacer for centering */}
                </div>
                
                {/* Buttons List */}
                <div 
                  className="px-4 pb-6 max-h-[300px] overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
                >
                  {/* Quick Reply Section */}
                  {quickReplyButtons.length > 0 && (
                    <div className="space-y-1">
                      {quickReplyButtons.map((btn, index) => (
                        <div 
                          key={`qr-${index}`}
                          className="flex items-center gap-3 py-3 text-[#0088cc] font-medium text-[15px] cursor-pointer hover:bg-gray-50 rounded-lg px-2 transition-colors"
                        >
                          {getButtonIcon(btn.type)}
                          <span>{btn.text || 'Quick Reply'}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Divider between Quick Replies and CTAs */}
                  {quickReplyButtons.length > 0 && ctaButtons.length > 0 && (
                    <div className="border-t border-gray-200 my-2" />
                  )}
                  
                  {/* CTA Section */}
                  {ctaButtons.length > 0 && (
                    <div className="space-y-1">
                      {ctaButtons.map((btn, index) => (
                        <div 
                          key={`cta-${index}`}
                          className="flex items-center gap-3 py-3 text-[#0088cc] font-medium text-[15px] cursor-pointer hover:bg-gray-50 rounded-lg px-2 transition-colors"
                        >
                          {getButtonIcon(btn.type)}
                          <span>{btn.text || 'Button'}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>

      <p className="text-center text-xs text-(--color-muted-gray) mt-3 opacity-60">
        This is how your message will appear on WhatsApp
      </p>
    </div>
  );
};

// ==================== MAIN COMPONENT ====================

const TemplatesPage = () => {
  // State
  const [templates, setTemplates] = useState<TemplateItem[]>([]);
  const [stats, setStats] = useState<TemplateStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 10;

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');

  // Multiple organizations: the account chosen in the header scopes the list, stats and sync (all
  // accounts when none) and is where a new template is created
  const { isMultiOrganization, selectedOrganizationId, requireAccount } = useOrganizations();
  const [listOrganizationId, setListOrganizationId] = useState(selectedOrganizationId);
  if (listOrganizationId !== selectedOrganizationId) {
    setListOrganizationId(selectedOrganizationId);
    setPage(1);
  }

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<TemplateItem | null>(null);
  const [previewTemplate, setPreviewTemplate] = useState<TemplateItem | null>(null);
  const [modalError, setModalError] = useState<string | null>(null);

  // Form data
  const [formData, setFormData] = useState<CreateTemplateData>({
    name: '',
    category: 'MARKETING',
    language: 'en',
    headerType: undefined,
    headerContent: '',
    bodyText: '',
    footerText: '',
    buttons: [],
  });

  // Media upload state
  const [mediaFile, setMediaFile] = useState<File | null>(null);
  const [mediaPreviewUrl, setMediaPreviewUrl] = useState<string | null>(null);
  const [mediaUploading, setMediaUploading] = useState(false);
  const [mediaError, setMediaError] = useState<string | null>(null);
  
  // Variable samples state
  const [variableSamples, setVariableSamples] = useState<VariableSample[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState<TemplateItem | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Submit confirmation
  const [submitConfirm, setSubmitConfirm] = useState<TemplateItem | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // ==================== FETCH DATA ====================

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const response = await templatesService.getAll({
        search: searchQuery || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        category: categoryFilter !== 'all' ? categoryFilter : undefined,
        organizationId: selectedOrganizationId,
        page,
        limit,
      });
      setTemplates(response.items);
      setTotalPages(response.meta.totalPages);
      setTotal(response.meta.total);
    } catch (err) {
      console.error('Failed to fetch templates:', err);
      setError('Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, statusFilter, categoryFilter, selectedOrganizationId, page]);

  const fetchStats = useCallback(async () => {
    try {
      const data = await templatesService.getStats(selectedOrganizationId);
      setStats(data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  }, [selectedOrganizationId]);

  useEffect(() => {
    fetchTemplates();
    fetchStats();
  }, [fetchTemplates, fetchStats]);

  // ==================== HANDLERS ====================

  const handleOpenModal = (template?: TemplateItem) => {
    if (template) {
      setEditingTemplate(template);
      setFormData({
        name: template.name,
        category: template.category,
        language: template.language,
        headerType: template.headerType,
        headerContent: template.headerContent || '',
        bodyText: template.bodyText,
        footerText: template.footerText || '',
        buttons: template.buttons || [],
      });
      // Load existing variable samples
      setVariableSamples(template.variableSamples || []);
    } else {
      if (!requireAccount('create a template')) return;
      setEditingTemplate(null);
      setFormData({
        name: '',
        category: 'MARKETING',
        language: 'en',
        headerType: undefined,
        headerContent: '',
        bodyText: '',
        footerText: '',
        buttons: [],
      });
      setVariableSamples([]);
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingTemplate(null);
    // Clear media state
    setMediaFile(null);
    setMediaPreviewUrl(null);
    setMediaError(null);
    setModalError(null);
    // Clear variable samples
    setVariableSamples([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle media file selection
  const handleMediaFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !formData.headerType) return;

    // Validate file type
    const allowedTypes: Record<string, string[]> = {
      IMAGE: ['image/jpeg', 'image/png'],
      VIDEO: ['video/mp4', 'video/3gpp'],
      DOCUMENT: ['application/pdf'],
    };

    const allowed = allowedTypes[formData.headerType];
    if (!allowed?.includes(file.type)) {
      setMediaError(`Invalid file type. Allowed: ${allowed?.join(', ') || 'none'}`);
      return;
    }

    // Validate file size
    const maxSizes: Record<string, number> = {
      IMAGE: 5 * 1024 * 1024, // 5 MB
      VIDEO: 16 * 1024 * 1024, // 16 MB
      DOCUMENT: 100 * 1024 * 1024, // 100 MB
    };

    const maxSize = maxSizes[formData.headerType];
    if (file.size > maxSize) {
      setMediaError(`File too large. Max size: ${maxSize / (1024 * 1024)} MB`);
      return;
    }

    setMediaError(null);
    setMediaFile(file);

    // Create local preview URL for images and videos
    if (formData.headerType === 'IMAGE' || formData.headerType === 'VIDEO') {
      const url = URL.createObjectURL(file);
      setMediaPreviewUrl(url);
    } else {
      setMediaPreviewUrl(null);
    }

    // Upload the file to Meta
    try {
      setMediaUploading(true);
      const result = await templatesService.uploadMedia(
        file,
        formData.headerType as 'IMAGE' | 'VIDEO' | 'DOCUMENT',
        // Multiple organizations: the template's account owns the upload
        isMultiOrganization
          ? editingTemplate
            ? { templateId: editingTemplate.id }
            : { organizationId: selectedOrganizationId }
          : undefined,
      );
      
      if (result.success && result.handle) {
        // Store the media handle in headerContent (use only the handle, not append)
        // Handle might contain newlines if multiple uploads happened, take only the last one
        const cleanHandle = result.handle.split('\n').pop() || result.handle;
        setFormData(prev => ({ ...prev, headerContent: cleanHandle }));
        setMediaError(null);
      } else {
        setMediaError(result.message || 'Failed to upload media');
        // Keep the local preview even if upload failed
      }
    } catch (err) {
      console.error('Media upload failed:', err);
      setMediaError(err instanceof Error ? err.message : 'Failed to upload media');
    } finally {
      setMediaUploading(false);
    }
  };

  // Clear media selection
  const handleClearMedia = () => {
    setMediaFile(null);
    setMediaPreviewUrl(null);
    setMediaError(null);
    setFormData(prev => ({ ...prev, headerContent: '' }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Get accepted file types for the file input
  const getAcceptedFileTypes = (): string => {
    switch (formData.headerType) {
      case 'IMAGE': return 'image/jpeg,image/png';
      case 'VIDEO': return 'video/mp4,video/3gpp';
      case 'DOCUMENT': return 'application/pdf';
      default: return '';
    }
  };

  // Validate form and return error message if invalid
  const validateForm = (): string | null => {
    // Required fields
    if (!formData.name.trim()) {
      return 'Template name is required';
    }
    if (!formData.bodyText.trim()) {
      return 'Body text is required';
    }

    // Validate variable positions
    const varPositionValidation = validateVariablePosition(formData.bodyText);
    if (!varPositionValidation.valid) {
      return varPositionValidation.error || "Variables can't be at the start or end of the template.";
    }

    // Validate media header has content uploaded
    if (formData.headerType && ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(formData.headerType)) {
      if (!formData.headerContent) {
        return `${formData.headerType.charAt(0) + formData.headerType.slice(1).toLowerCase()} header requires a file to be uploaded.`;
      }
    }

    // Validate text header has content
    if (formData.headerType === 'TEXT' && !formData.headerContent?.trim()) {
      return 'Text header requires header text.';
    }

    // Validate all variable samples are filled
    if (variableSamples.length > 0) {
      const missingSamples = variableSamples.filter(s => !s.sample.trim());
      if (missingSamples.length > 0) {
        return `Please provide sample values for all variables: ${missingSamples.map(s => `{{${s.variable}}}`).join(', ')}`;
      }
    }

    // Check for duplicate button texts
    if (formData.buttons && formData.buttons.length > 0) {
      const buttonTexts = formData.buttons.map(btn => btn.text?.trim().toLowerCase()).filter(Boolean);
      const uniqueTexts = new Set(buttonTexts);
      if (buttonTexts.length !== uniqueTexts.size) {
        return 'Button texts must be unique. Please use different text for each button.';
      }

      // Validate phone buttons have required data
      const phoneButtons = formData.buttons.filter(btn => btn.type === 'PHONE');
      for (const btn of phoneButtons) {
        if (!btn.countryCode || !btn.phoneNumber) {
          return `Phone button "${btn.text}" requires both country code and phone number.`;
        }
        // Validate phone number format (should be digits only, 7-15 digits for valid international numbers)
        const phoneDigits = btn.phoneNumber.replace(/[^\d]/g, '');
        if (phoneDigits.length < 7 || phoneDigits.length > 15) {
          return `Phone button "${btn.text}" has an invalid phone number. Must be 7-15 digits (without country code or leading zeros).`;
        }
      }

      // Validate URL buttons have required data
      const urlButtons = formData.buttons.filter(btn => btn.type === 'URL');
      for (const btn of urlButtons) {
        if (!btn.url) {
          return `URL button "${btn.text}" requires a URL.`;
        }
        // Basic URL validation
        try {
          new URL(btn.url);
        } catch {
          return `URL button "${btn.text}" has an invalid URL format.`;
        }
      }

      // Validate copy code buttons have required data
      const copyCodeButtons = formData.buttons.filter(btn => btn.type === 'COPY_CODE');
      for (const btn of copyCodeButtons) {
        if (!btn.offerCode) {
          return `Copy code button "${btn.text}" requires an offer code.`;
        }
      }
    }

    return null;
  };

  // Check if form is valid (for button state)
  const formValidationError = validateForm();
  const isFormValid = formValidationError === null;

  const handleSave = async () => {
    const validationError = validateForm();
    if (validationError) {
      setModalError(validationError);
      return;
    }

    try {
      setSaving(true);
      setModalError(null);

      const payload: CreateTemplateData = {
        name: formData.name.trim(),
        category: formData.category,
        language: formData.language,
        bodyText: formData.bodyText.trim(),
        ...(formData.headerType && { headerType: formData.headerType }),
        ...(formData.headerContent && { headerContent: formData.headerContent.trim() }),
        ...(formData.footerText && { footerText: formData.footerText.trim() }),
        ...(formData.buttons && formData.buttons.length > 0 && { buttons: formData.buttons }),
        ...(variableSamples.length > 0 && { variableSamples }),
      };

      if (editingTemplate) {
        await templatesService.update(editingTemplate.id, payload);
        setSuccess('Template updated successfully');
      } else {
        await templatesService.create({
          ...payload,
          ...(selectedOrganizationId && { organizationId: selectedOrganizationId }),
        });
        setSuccess('Template created successfully');
      }

      handleCloseModal();
      fetchTemplates();
      fetchStats();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: unknown) {
      console.error('Failed to save template:', err);
      setModalError(err instanceof Error ? err.message : 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;

    try {
      setDeleting(true);
      await templatesService.delete(deleteConfirm.id);
      setSuccess('Template deleted successfully');
      setDeleteConfirm(null);
      fetchTemplates();
      fetchStats();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: unknown) {
      console.error('Failed to delete template:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete template');
    } finally {
      setDeleting(false);
    }
  };

  const handleSubmit = async () => {
    if (!submitConfirm) return;

    try {
      setSubmitting(true);
      await templatesService.submitForApproval(submitConfirm.id);
      setSuccess('Template submitted for approval');
      setSubmitConfirm(null);
      fetchTemplates();
      fetchStats();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: unknown) {
      console.error('Failed to submit template:', err);
      setError(err instanceof Error ? err.message : 'Failed to submit template');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDuplicate = async (template: TemplateItem) => {
    if (!requireAccount('duplicate a template')) return;
    setFormData({
      name: `${template.name} (Copy)`,
      category: template.category,
      language: template.language,
      headerType: template.headerType,
      headerContent: template.headerContent || '',
      bodyText: template.bodyText,
      footerText: template.footerText || '',
      buttons: template.buttons || [],
    });
    setEditingTemplate(null);
    setIsModalOpen(true);
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      const result = await templatesService.syncFromMeta(selectedOrganizationId);
      setSuccess(`Synced ${result.synced} new, updated ${result.updated} templates`);
      fetchTemplates();
      fetchStats();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: unknown) {
      console.error('Failed to sync templates:', err);
      setError(err instanceof Error ? err.message : 'Failed to sync templates from Meta');
    } finally {
      setSyncing(false);
    }
  };

  // ==================== RENDER ====================

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-white flex items-center gap-3">
            <FileText className="w-8 h-8 text-primary-400" />
            Message Templates
          </h1>
          <p className="text-sm text-(--color-muted-gray) mt-1">
            Create and manage WhatsApp message templates for your campaigns
          </p>
        </div>
        <div className="flex items-center gap-3">
          <a
            href="https://business.facebook.com/wa/manage/message-templates/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-(--color-muted-gray) hover:text-white bg-white/5 hover:bg-white/10 border border-(--color-border-gray) rounded-lg transition-colors"
          >
            <ExternalLink className="w-4 h-4" />
            Meta Manager
          </a>
          <Button variant="secondary" onClick={handleSync} disabled={syncing}>
            {syncing ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Sync from Meta
          </Button>
          <Button onClick={() => handleOpenModal()}>
            <Plus className="w-4 h-4 mr-2" />
            Create Template
          </Button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-linear-to-br from-orange-500/20 to-orange-600/10 flex items-center justify-center">
                <FileText className="w-5 h-5 text-orange-500" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-white">{stats.total}</p>
                <p className="text-xs text-(--color-muted-gray)">Total</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-linear-to-br from-gray-500/20 to-gray-600/10 flex items-center justify-center">
                <FileText className="w-5 h-5 text-gray-400" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-white">{stats.byStatus.draft}</p>
                <p className="text-xs text-(--color-muted-gray)">Draft</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-linear-to-br from-yellow-500/20 to-yellow-600/10 flex items-center justify-center">
                <Clock className="w-5 h-5 text-yellow-500" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-white">{stats.byStatus.pending}</p>
                <p className="text-xs text-(--color-muted-gray)">Pending</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-linear-to-br from-green-500/20 to-green-600/10 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-white">{stats.byStatus.approved}</p>
                <p className="text-xs text-(--color-muted-gray)">Approved</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-linear-to-br from-red-500/20 to-red-600/10 flex items-center justify-center">
                <XCircle className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-white">{stats.byStatus.rejected}</p>
                <p className="text-xs text-(--color-muted-gray)">Rejected</p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-(--color-muted-gray)" />
            <Input
              placeholder="Search templates..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(1);
              }}
              className="pl-10"
            />
          </div>
          <div className="w-40">
            <Select
              options={TEMPLATE_FILTER_STATUS_OPTIONS}
              value={statusFilter}
              onChange={(value) => {
                setStatusFilter(Array.isArray(value) ? value[0] : value);
                setPage(1);
              }}
              placeholder="Status"
            />
          </div>
          <div className="w-40">
            <Select
              options={TEMPLATE_FILTER_CATEGORY_OPTIONS}
              value={categoryFilter}
              onChange={(value) => {
                setCategoryFilter(Array.isArray(value) ? value[0] : value);
                setPage(1);
              }}
              placeholder="Category"
            />
          </div>
        </div>
      </Card>

      {/* Alerts */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="flex items-center gap-3 p-4 rounded-lg bg-red-500/10 border border-red-500/30"
          >
            <AlertCircle className="w-5 h-5 text-red-400 shrink-0" />
            <p className="text-sm text-red-300 flex-1">{error}</p>
            <button onClick={() => setError(null)} className="text-red-400 hover:text-red-300">
              <X className="w-4 h-4" />
            </button>
          </motion.div>
        )}

        {success && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="flex items-center gap-3 p-4 rounded-lg bg-green-500/10 border border-green-500/30"
          >
            <CheckCircle className="w-5 h-5 text-green-400 shrink-0" />
            <p className="text-sm text-green-300 flex-1">{success}</p>
            <button onClick={() => setSuccess(null)} className="text-green-400 hover:text-green-300">
              <X className="w-4 h-4" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Templates List */}
      {loading ? (
        <Card className="p-8">
          <div className="flex items-center justify-center gap-3">
            <Loader2 className="w-5 h-5 text-orange-500 animate-spin" />
            <span className="text-(--color-muted-gray)">Loading templates...</span>
          </div>
        </Card>
      ) : templates.length === 0 ? (
        <Card className="p-8">
          <div className="text-center">
            <div className="w-16 h-16 rounded-2xl bg-linear-to-br from-orange-500/20 to-orange-600/10 flex items-center justify-center mx-auto mb-4">
              <FileText className="w-8 h-8 text-orange-500" />
            </div>
            <h3 className="text-lg font-medium text-white mb-2">No Templates Yet</h3>
            <p className="text-sm text-(--color-muted-gray) mb-6 max-w-md mx-auto">
              Create message templates to send marketing campaigns, utility messages, or authentication codes to your contacts.
            </p>
            <Button onClick={() => handleOpenModal()}>
              <Plus className="w-4 h-4 mr-2" />
              Create Your First Template
            </Button>
          </div>
        </Card>
      ) : (
        <>
          <Card className="overflow-hidden">
            <div className="divide-y divide-(--color-border-gray)">
              {templates.map((template) => (
                <TemplateItemRow
                  key={template.id}
                  template={template}
                  onEdit={() => handleOpenModal(template)}
                  onDelete={() => setDeleteConfirm(template)}
                  onSubmit={() => setSubmitConfirm(template)}
                  onDuplicate={() => handleDuplicate(template)}
                  onPreview={() => setPreviewTemplate(template)}
                />
              ))}
            </div>
          </Card>

          {/* Pagination */}
          {totalPages > 1 && (
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
              onPageSizeChange={() => {}}
              totalItems={total}
              pageSize={limit}
            />
          )}
        </>
      )}

      {/* Create/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingTemplate ? 'Edit Template' : 'Create Template'}
        subtitle={editingTemplate ? 'Update template details' : 'Create a new message template'}
        size="xl"
        showCloseButton
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-6 max-h-[calc(100vh-200px)] overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          {/* Form */}
          <div className="space-y-5">
            <Input
              label="Template Name"
              placeholder="e.g., welcome_message"
              value={formData.name}
              onChange={(e) => {
                // Auto-format: lowercase, replace spaces with underscores, remove invalid chars
                const formatted = e.target.value
                  .toLowerCase()
                  .replace(/\s+/g, '_')           // Replace spaces with underscores
                  .replace(/[^a-z0-9_]/g, '')     // Remove invalid characters
                  .replace(/_+/g, '_');           // Replace multiple underscores with single
                setFormData((prev) => ({ ...prev, name: formatted }));
              }}
              required
              hint="Use lowercase letters, numbers, and underscores only"
            />

            <div className="grid grid-cols-2 gap-4">
              <Select
                label="Category"
                options={TEMPLATE_CATEGORY_OPTIONS}
                value={formData.category}
                onChange={(value) => setFormData((prev) => ({ ...prev, category: (Array.isArray(value) ? value[0] : value) as CreateTemplateData['category'] }))}
              />
              <Select
                label="Language"
                options={TEMPLATE_LANGUAGE_OPTIONS}
                value={formData.language || 'en'}
                onChange={(value) => setFormData((prev) => ({ ...prev, language: Array.isArray(value) ? value[0] : value }))}
                searchable
                placeholder="Search languages..."
              />
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Select
                  label="Header Type"
                  options={TEMPLATE_HEADER_TYPE_OPTIONS}
                  value={formData.headerType || ''}
                  onChange={(value) => {
                    const v = Array.isArray(value) ? value[0] : value;
                    // Clear media when header type changes
                    handleClearMedia();
                    setFormData((prev) => ({
                      ...prev,
                      headerType: v ? (v as CreateTemplateData['headerType']) : undefined,
                      headerContent: '',
                    }));
                  }}
                />
                {formData.headerType === 'TEXT' && (
                  <Input
                    label="Header Text"
                    placeholder="Header content (max 60 chars)"
                    value={formData.headerContent || ''}
                    onChange={(e) => setFormData((prev) => ({ ...prev, headerContent: e.target.value }))}
                    maxLength={60}
                  />
                )}
              </div>

              {/* Media Upload for IMAGE, VIDEO, DOCUMENT headers */}
              {formData.headerType && ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(formData.headerType) && (
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-(--color-soft-gray)">
                    {formData.headerType === 'IMAGE' && 'Header Image'}
                    {formData.headerType === 'VIDEO' && 'Header Video'}
                    {formData.headerType === 'DOCUMENT' && 'Header Document'}
                    <span className="text-red-400 ml-1">*</span>
                  </label>
                  
                  {/* Hidden file input */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={getAcceptedFileTypes()}
                    onChange={handleMediaFileChange}
                    className="hidden"
                  />

                  {/* Upload area or preview */}
                  {!mediaFile ? (
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full p-6 border-2 border-dashed border-(--color-border-gray) rounded-lg hover:border-orange-500/50 transition-colors group"
                    >
                      <div className="flex flex-col items-center gap-2 text-(--color-muted-gray) group-hover:text-white transition-colors">
                        <Upload className="w-8 h-8" />
                        <span className="text-sm font-medium">
                          Click to upload {formData.headerType.toLowerCase()}
                        </span>
                        <span className="text-xs">
                          {formData.headerType === 'IMAGE' && 'JPG, PNG (max 5 MB)'}
                          {formData.headerType === 'VIDEO' && 'MP4, 3GPP (max 16 MB)'}
                          {formData.headerType === 'DOCUMENT' && 'PDF (max 100 MB)'}
                        </span>
                      </div>
                    </button>
                  ) : (
                    <div className="relative rounded-lg overflow-hidden border border-(--color-border-gray) bg-(--color-charcoal-black)">
                      {/* Image preview */}
                      {formData.headerType === 'IMAGE' && mediaPreviewUrl && (
                        <img 
                          src={mediaPreviewUrl} 
                          alt="Header preview" 
                          className="w-full h-40 object-cover"
                        />
                      )}

                      {/* Video preview */}
                      {formData.headerType === 'VIDEO' && mediaPreviewUrl && (
                        <div className="relative h-40 bg-black flex items-center justify-center">
                          <video 
                            src={mediaPreviewUrl} 
                            className="max-h-full max-w-full"
                          />
                          <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                            <Play className="w-10 h-10 text-white/80" />
                          </div>
                        </div>
                      )}

                      {/* Document preview */}
                      {formData.headerType === 'DOCUMENT' && (
                        <div className="h-20 flex items-center gap-3 px-4">
                          <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
                            <File className="w-5 h-5 text-red-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-white truncate">{mediaFile.name}</p>
                            <p className="text-xs text-(--color-muted-gray)">
                              {(mediaFile.size / 1024).toFixed(1)} KB
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Upload status overlay */}
                      {mediaUploading && (
                        <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                          <div className="flex items-center gap-2 text-white">
                            <Loader2 className="w-5 h-5 animate-spin" />
                            <span className="text-sm">Uploading to Meta...</span>
                          </div>
                        </div>
                      )}

                      {/* Clear button */}
                      <button
                        type="button"
                        onClick={handleClearMedia}
                        className="absolute top-2 right-2 p-1.5 rounded-full bg-black/50 hover:bg-black/70 text-white transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>

                      {/* Upload success indicator */}
                      {formData.headerContent && !mediaUploading && (
                        <div className="absolute bottom-2 left-2 flex items-center gap-1 px-2 py-1 rounded-full bg-green-500/20 text-green-400 text-xs">
                          <CheckCircle className="w-3 h-3" />
                          <span>Uploaded</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Error message */}
                  {mediaError && (
                    <div className="flex items-center gap-2 text-red-400 text-sm">
                      <AlertCircle className="w-4 h-4 shrink-0" />
                      <span>{mediaError}</span>
                    </div>
                  )}

                  {/* Info about media requirements */}
                  <p className="text-xs text-(--color-muted-gray)">
                    {formData.headerType === 'IMAGE' && 'Recommended: 800x418px for landscape, 800x800px for square'}
                    {formData.headerType === 'VIDEO' && 'Recommended: MP4 format, H.264 codec, max 16 MB'}
                    {formData.headerType === 'DOCUMENT' && 'Only PDF files are supported for document headers'}
                  </p>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-(--color-soft-gray) mb-2">
                Body Text <span className="text-red-400">*</span>
              </label>
              <BodyTextEditor
                value={formData.bodyText}
                onChange={(value) => setFormData((prev) => ({ ...prev, bodyText: value }))}
                maxLength={1024}
                placeholder="Enter your message body..."
                variableSamples={variableSamples}
                onVariableSamplesChange={setVariableSamples}
              />
            </div>

            <Input
              label="Footer Text"
              placeholder="Optional footer (max 60 chars)"
              value={formData.footerText || ''}
              onChange={(e) => setFormData((prev) => ({ ...prev, footerText: e.target.value }))}
              maxLength={60}
            />

            <div>
              <label className="block text-sm font-medium text-(--color-soft-gray) mb-2">
                Buttons (Optional)
              </label>
              <ButtonEditor
                buttons={formData.buttons}
                onChange={(buttons) => setFormData((prev) => ({ ...prev, buttons }))}
              />
            </div>
          </div>

          {/* Preview */}
          <div className="lg:border-l lg:border-(--color-border-gray) lg:pl-6 lg:sticky lg:top-0 lg:self-start">
            <TemplatePreview template={formData} mediaPreviewUrl={mediaPreviewUrl} />
          </div>
        </div>

        {/* Modal Error */}
        {modalError && (
          <div className="mx-6 mb-4 flex items-center gap-3 p-3 rounded-lg bg-red-500/10 border border-red-500/30">
            <AlertCircle className="w-5 h-5 text-red-400 shrink-0" />
            <p className="text-sm text-red-300 flex-1">{modalError}</p>
            <button onClick={() => setModalError(null)} className="text-red-400 hover:text-red-300">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-(--color-border-gray)">
          {/* Validation message */}
          <div className="flex-1">
            {!isFormValid && formValidationError && (
              <p className="text-xs text-orange-400 flex items-center gap-1">
                <AlertCircle className="w-3 h-3 shrink-0" />
                <span className="truncate">{formValidationError}</span>
              </p>
            )}
          </div>
          
          <div className="flex items-center gap-3">
            <Button variant="secondary" onClick={handleCloseModal} disabled={saving}>
              Cancel
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={saving || !isFormValid || mediaUploading}
              title={!isFormValid ? formValidationError || 'Please fix validation errors' : undefined}
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : mediaUploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>{editingTemplate ? 'Update Template' : 'Create Template'}</>
              )}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Preview Modal */}
      <Modal
        isOpen={!!previewTemplate}
        onClose={() => setPreviewTemplate(null)}
        title="Template Preview"
        subtitle={previewTemplate?.name}
        showCloseButton
      >
        <div className="p-6">
          {previewTemplate && <TemplatePreview template={previewTemplate} />}
          
          <div className="mt-6 p-4 rounded-lg bg-white/5 border border-white/10 space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-(--color-muted-gray)">Status:</span>
              <StatusBadge status={previewTemplate?.status || 'DRAFT'} />
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-(--color-muted-gray)">Category:</span>
              <CategoryBadge category={previewTemplate?.category || 'MARKETING'} />
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-(--color-muted-gray)">Language:</span>
              <span className="text-white">{previewTemplate?.language}</span>
            </div>
            {previewTemplate?.waTemplateId && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-(--color-muted-gray)">Meta ID:</span>
                <span className="text-white font-mono text-xs">{previewTemplate.waTemplateId}</span>
              </div>
            )}
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={handleDelete}
        title="Delete Template"
        description={`Are you sure you want to delete the "${deleteConfirm?.name}" template? This action cannot be undone.`}
        confirmText="Delete Template"
        variant="danger"
        isLoading={deleting}
      />

      {/* Submit Confirmation */}
      <ConfirmDialog
        isOpen={!!submitConfirm}
        onClose={() => setSubmitConfirm(null)}
        onConfirm={handleSubmit}
        title="Submit for Approval"
        description={`Are you sure you want to submit the "${submitConfirm?.name}" template for Meta approval? Once submitted, you cannot edit the template until it's approved or rejected.`}
        confirmText="Submit for Approval"
        variant="success"
        isLoading={submitting}
      />
    </div>
  );
};

export default TemplatesPage;

